'use client';

import Image from 'next/image';
import { type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import spotifyHandoffDevice from '@/../public/_n/images/dtc-partners/spotify-handoff-device.png';
import handoffSkippedQr from '@/../public/_n/images/onboarding/handoff_skipped_qr.png';
import messages from '@/components/SimplifiedSignup/Handoff/messages';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState } from '@/hooks/store/useAppState';
import DownloadOnTheAppStore from 'icons/apple-store.svg';
import GetItOnGooglePlay from 'icons/download-google-play.svg';

import { useScreenDidMount } from '../../hooks';
import { useSisu } from '../../Provider';
import { isFormattedMessage, type MessageNode } from '../../utils';
import {
	AppDownloadContainer,
	AppDownloadItemContainer,
	AppImageWrapper,
	AppStoreIconLink,
	HandoffContainer,
	HandoffPageWrap,
	HandoffSubheading,
	HandoffWelcomeContainer,
	QrCodeImageContainer,
	QrCodeImageWrapper,
	QRCodeWrapper,
	SocialWrapper,
	ValuePropsWrapper,
} from './styles';

export type HandoffScreenProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-handoff"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * ```tsx
	 * null
	 * ```
	 */
	header?: ReactElement | null;

	title?: MessageNode | null;
	subtitle?: MessageNode | null;

	qrCodeImage?: ReactElement;
	qrCodeInstructions?: MessageNode;
	/**
	 * Set to `null` to hide the "Download on the App Store" icon.
	 * @defaultValue
	 * ```tsx
	 * <DownloadOnTheAppStore />
	 * ```
	 */
	appleAppStoreIcon?: ReactElement | null;

	/**
	 * Set to `null` to hide the "Get it on Google Play" icon.
	 * @defaultValue
	 * ```tsx
	 * <GetItOnGooglePlay />
	 * ```
	 */
	googlePlayStoreIcon?: ReactElement | null;

	appImage?: ReactElement;

	appStoreUrl?: string;

	valuePropsList?: ReactElement | null;
};

export default function HandoffScreen({
	'data-testid': testId = 'sisu-handoff',

	header = null,
	title,
	subtitle,
	appleAppStoreIcon = <DownloadOnTheAppStore role="img" />,
	googlePlayStoreIcon = <GetItOnGooglePlay role="img" />,

	appStoreUrl = 'https://calm.onelink.me/Pf5F/oislygnz',

	qrCodeImage = (
		<Image
			alt="Scan QR code to download Calm app"
			fill
			src={handoffSkippedQr}
			style={{ objectFit: 'contain' }}
			unoptimized
		/>
	),
	qrCodeInstructions,
	appImage = (
		<Image alt="App image" fill src={spotifyHandoffDevice} style={{ objectFit: 'contain' }} unoptimized />
	),

	valuePropsList = null,
}: HandoffScreenProps) {
	const { logEvent } = useAnalytics();
	const { activeScreen, backgroundColor, flowName, isWithinModal, logPageViewedEvent } = useSisu();
	const { formatMessage } = useIntl();
	const device = useDeviceState();

	const isAndroid = device.os === 'AndroidOS';

	let appStoreIcon: ReactElement | null = null;
	if (device.isMobile) {
		appStoreIcon = isAndroid ? googlePlayStoreIcon : appleAppStoreIcon;
	}

	const handleAppStoreIconLinkClick = () => {
		logEvent({
			eventName: `${flowName} : Install : Clicked`,
			eventProps: {
				source: flowName,
				platform: device?.os ?? null,
			},
		});
	};

	useScreenDidMount('handoff', () => {
		logPageViewedEvent('handoff');
	});

	if (activeScreen !== 'handoff') {
		return null;
	}

	if (typeof title === 'undefined') {
		title = formatMessage(messages.handoffHeader);
	}

	if (typeof subtitle === 'undefined') {
		subtitle = formatMessage(messages.subHeading);
	}

	if (typeof qrCodeInstructions === 'undefined') {
		qrCodeInstructions = formatMessage(messages.qrCodeInstructions);
	}

	return (
		<>
			{header}
			<HandoffPageWrap
				backgroundColor={backgroundColor}
				data-testid={testId}
				isWithinModal={isWithinModal}
				isCompact={isWithinModal}
			>
				<HandoffContainer>
					<HandoffWelcomeContainer>
						{title && isFormattedMessage(title) ? (
							<Text
								aria-live="polite"
								el="h1"
								role="alert"
								size={FontSizes['2xl']}
								weight={FontWeights.Medium}
							>
								{title}
							</Text>
						) : (
							title
						)}
						{subtitle && isFormattedMessage(subtitle) ? (
							<HandoffSubheading align="center" el="p" size={FontSizes.lg} weight={FontWeights.Regular}>
								{subtitle}
							</HandoffSubheading>
						) : (
							subtitle
						)}
						{appStoreIcon && (
							<AppStoreIconLink href={appStoreUrl} onClick={handleAppStoreIconLinkClick}>
								{appStoreIcon}
							</AppStoreIconLink>
						)}
					</HandoffWelcomeContainer>
					<AppDownloadContainer>
						<AppDownloadItemContainer>
							<QRCodeWrapper>
								{isFormattedMessage(qrCodeInstructions) ? (
									<Text align="center" el="h2" size={FontSizes.lg} weight={FontWeights.Medium}>
										{qrCodeInstructions}
									</Text>
								) : (
									qrCodeInstructions
								)}
								<QrCodeImageContainer>
									<QrCodeImageWrapper>{qrCodeImage}</QrCodeImageWrapper>
								</QrCodeImageContainer>
								{(appleAppStoreIcon || googlePlayStoreIcon) && (
									<SocialWrapper>
										{appleAppStoreIcon}
										{googlePlayStoreIcon}
									</SocialWrapper>
								)}
							</QRCodeWrapper>
						</AppDownloadItemContainer>
						<AppDownloadItemContainer>
							<AppImageWrapper>{appImage}</AppImageWrapper>
						</AppDownloadItemContainer>
					</AppDownloadContainer>
					{valuePropsList && <ValuePropsWrapper>{valuePropsList}</ValuePropsWrapper>}
				</HandoffContainer>
			</HandoffPageWrap>
		</>
	);
}
